import { useCallback, useState } from 'react';
import { SharedValue, interpolate, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';

import { Currency } from '../utils';

const ANIMATION_DURATION = 200;

const CARD_OFFSETS = [0, -50, -100, -150, -200, -250];
const CARD_SCALES = [1, 0.97, 0.93, 0.9, 0.87, 0.84];
const MAX_POSITION = CARD_OFFSETS.length - 1;

interface CardStackAnimationReturn {
    cardOrder: Currency[];
    activeCurrency: Currency;
    animationProgress: SharedValue<number>;
    cardCount: SharedValue<number>;
    handleCardPress: (cardType: Currency) => void;
}

export const useCardPositionStyle = (
    position: number,
    animationProgress: SharedValue<number>,
    cardCount: SharedValue<number>
) => {
    return useAnimatedStyle(() => {
        'worklet';
        const clampedPos = Math.min(position, MAX_POSITION);
        const isLast = position >= cardCount.value - 1;

        const fromOffset = isLast ? CARD_OFFSETS[0] : CARD_OFFSETS[Math.min(position + 1, MAX_POSITION)];
        const fromScale = isLast ? CARD_SCALES[0] : CARD_SCALES[Math.min(position + 1, MAX_POSITION)];

        const translateY = interpolate(animationProgress.value, [0, 1], [CARD_OFFSETS[clampedPos], fromOffset]);
        const scale = interpolate(animationProgress.value, [0, 1], [CARD_SCALES[clampedPos], fromScale]);

        return {
            transform: [{ translateY }, { scale }],
            zIndex: MAX_POSITION + 1 - clampedPos,
        };
    });
};

export const useCardStackAnimation = (currencies: Currency[]): CardStackAnimationReturn => {
    const socialCurrencyStore = useSocialCurrencyStore();

    const [cardOrder, setCardOrder] = useState<Currency[]>(currencies);

    const activeCurrency: Currency = cardOrder[0];

    const animationProgress = useSharedValue(0);
    const cardCount = useSharedValue(currencies.length);

    const handleCardPress = useCallback(
        (cardType: Currency) => {
            const targetIndex = cardOrder.indexOf(cardType);
            if (targetIndex <= 0) {
                return;
            }
            setCardOrder(prev => [...prev.slice(targetIndex), ...prev.slice(0, targetIndex)]);

            if (Object.values(SocialCurrency).includes(cardType as SocialCurrency)) {
                socialCurrencyStore.actions.setActiveCurrency(cardType as SocialCurrency);
            }

            animationProgress.value = 1;
            animationProgress.value = withTiming(0, { duration: ANIMATION_DURATION });
        },
        [cardOrder, animationProgress, socialCurrencyStore.actions]
    );

    return {
        cardOrder,
        activeCurrency,
        animationProgress,
        cardCount,
        handleCardPress,
    };
};
